import React, { useEffect, useMemo, useState } from "react";
import { MRT_ColumnFiltersState } from "material-react-table";
import { useMutation } from "@tanstack/react-query";
import { AdaptedCollectorsMissingDocuments, postMissingDocuments } from "api";
import { usePagination, useCustomRowSelection } from "hooks";
import { createCustomRowSelectionColumn } from "components/table/customColumns";
import { LOCAL_STORAGE_KEYS } from "consts";
import { MonthCell } from "../components";
import { getFiscalYearMonthHeaders, getStoredColumnFilters, transformMissingDocumentsResponse } from "../helpers";
import { MISSING_DOCUMENTS_STATIC_COLUMNS } from "../consts";
import { UseMissingDocumentsTableProps } from "./types";

export const useMissingDocumentsTable = ({
  defaultObject,
  currentFY,
  companyMonths,
  allMonths,
  onFetchComplete,
}: UseMissingDocumentsTableProps) => {
  const {
    pagination,
    paginatedQuery,
    onPageChange,
    onSizeChange,
    updateFromApiResponse,
    resetPage,
    uiPage,
  } = usePagination({
    localStorageKey: LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_PAGINATION,
    initialPage: 0,
    initialSize: 50,
  });

  const {
    mutateAsync: postMissingDocumentsRequest,
    isLoading: loadingMissingDocuments,
  } = useMutation(postMissingDocuments);

  const [currentMissingData, setCurrentMissingData] = useState<any[]>([]);
  const [allCurrentCollectorsIds, setAllCurrentCollectorsIds] = useState<
    string[]
  >([]);
  const [monthHeaderFY, setMonthHeaderFY] = useState<string>(
    currentFY?.substring(0, 4) || "",
  );
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    getStoredColumnFilters,
  );
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [isTableRequestInProgress, setIsTableRequestInProgress] =
    useState<boolean>(false);
  const [selectedMonthCell, setSelectedMonthCell] = useState<{
    month: string;
    year: number;
  }>({ month: "", year: Number(currentFY?.substring(0, 4)) });
  const [collectorMissingDocuments, setCollectorMissingDocuments] =
    useState<AdaptedCollectorsMissingDocuments>();
  const [openMonthView, setOpenMonthView] = useState<boolean>(false);

  const { size, totalRows } = pagination;

  const {
    selectedRows,
    allRowsSelected,
    totalSelectedRows,
    handleRowToggle,
    handleSelectAll,
    resetSelection,
  } = useCustomRowSelection({
    totalRows,
    data: currentMissingData,
  });

  useEffect(() => {
    setMonthHeaderFY(currentFY?.substring(0, 4) || "");
  }, [currentFY]);

  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COLUMN_FILTERS,
      JSON.stringify(columnFilters),
    );
  }, [columnFilters]);

  const monthHeaders = useMemo(
    () => getFiscalYearMonthHeaders(companyMonths, Number(monthHeaderFY)),
    [companyMonths, monthHeaderFY],
  );

  const monthColumns = useMemo(
    () =>
      monthHeaders.map(({ header: stringMonthValue, year: monthYear }) => {
        const monthKey = stringMonthValue.substring(0, 3);
        const numberMonthValue = allMonths.indexOf(monthKey) + 1;

        return {
          id: `${monthHeaderFY}-${numberMonthValue}`,
          header: stringMonthValue,
          accessorFn: (row: any) => row.months?.[monthKey],
          Cell: ({ cell, row }: { cell: any; row: any }) => {
            const { collector, months, unit } = row.original;
            const handleMonthSelectedOpen = () => {
              setSelectedMonthCell({ month: monthKey, year: monthYear });
              setCollectorMissingDocuments({
                unit,
                id: collector.id,
                collector,
                days: allMonths.reduce((acc: any, month: string) => {
                  const monthIndex = allMonths.indexOf(month) + 1;
                  if (!acc[monthIndex]) {
                    acc[monthIndex] = months[month]
                      ? months[month].days.reduce(
                          (dayAcc: any, currentDay: any) => ({
                            ...dayAcc,
                            [currentDay.day]: {
                              usage: currentDay.usage ?? null,
                              dayBillType: currentDay.dayBillType ?? null,
                            },
                          }),
                          {},
                        )
                      : null;
                  }
                  return acc;
                }, {}),
                months: allMonths.reduce((acc: any, month: string) => {
                  if (!acc[allMonths.indexOf(month) + 1]) {
                    acc[allMonths.indexOf(month) + 1] = {
                      usage: months[month]?.monthUsage ?? null,
                      monthBillType: months[month]?.monthBillType ?? null,
                    };
                  }
                  return acc;
                }, {}),
              });
              setOpenMonthView(true);
            };

            return (
              <MonthCell
                cell={cell}
                monthYear={monthYear}
                numberMonthValue={numberMonthValue}
                isMonthModalOpen={openMonthView}
                handleMonthSelectedOpen={handleMonthSelectedOpen}
              />
            );
          },
          enableSorting: false,
          minSize: 120,
          size: 120,
        };
      }),
    [monthHeaders, monthHeaderFY, openMonthView, allMonths],
  );

  const customRowSelectionColumn = createCustomRowSelectionColumn({
    selectedRows,
    allRowsSelected,
    totalRows,
    totalSelectedRows,
    tooltip:
      "Select All selects all collectors across all pages for the current filter.",
    onRowToggle: handleRowToggle,
    onSelectAll: handleSelectAll,
  });

  const columns = [
    ...customRowSelectionColumn,
    ...MISSING_DOCUMENTS_STATIC_COLUMNS,
    ...monthColumns,
  ];

  const handleUpdateMissingDocuments = async (payloadFromPagination?: any) => {
    setIsTableRequestInProgress(true);
    const missingDocRequestBody = payloadFromPagination ?? {
      ...defaultObject.current,
      ...paginatedQuery,
    };

    try {
      await postMissingDocumentsRequest(missingDocRequestBody, {
        onSuccess: (response: any) => {
          const { data } = response;
          const responseArray = Array.isArray(data?.response)
            ? data.response
            : Array.isArray((data as any)?.content)
            ? (data as any).content
            : [];
          updateFromApiResponse(data?.pageResult);
          const parsedData = transformMissingDocumentsResponse(responseArray, allMonths);
          setCurrentMissingData(parsedData);
          setAllCurrentCollectorsIds(data?.allCollectorIds ?? []);
        },
        onError: () => {
          // implement error handling
        },
      });
      resetSelection();
      onFetchComplete?.();
    } finally {
      setIsTableRequestInProgress(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    onPageChange(newPage);
    handleUpdateMissingDocuments({
      ...defaultObject.current,
      page: newPage - 1,
      size,
    });
  };

  const handleSizeChange = (newSize: number) => {
    onSizeChange(newSize);
    handleUpdateMissingDocuments({
      ...defaultObject.current,
      page: 0,
      size: newSize,
    });
  };

  return {
    currentMissingData,
    allCurrentCollectorsIds,
    loadingMissingDocuments,
    isTableRequestInProgress,
    columns,
    companyMonths,
    openMonthView,
    setOpenMonthView,
    selectedMonthCell,
    collectorMissingDocuments,
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    pagination,
    uiPage,
    handlePageChange,
    handleSizeChange,
    selectedRows,
    allRowsSelected,
    totalSelectedRows,
    handleUpdateMissingDocuments,
    resetSelection,
    resetPage,
  };
};
